class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    this.display = this.querySelector("[data-quantity-display]");
    this.minusBtn = this.querySelector('[data-action="decrease"]');
    this.plusBtn = this.querySelector('[data-action="increase"]');
    this.customEvent = this.dataset.customEvent || null; // Optional custom event to dispatch
    this.loadingEvent = this.dataset.loadingEvent || null; // Optional loading event to dispatch when update starts
    this.allowRemove = this.dataset.allowRemove !== "false"; // By default true, unless explicitly set to 'false'

    if (!this.display || !this.key) return;

    this.quantity = parseInt(this.display.textContent, 10) || 1;

    this.minusBtn?.addEventListener("click", () => this.changeQuantity(-1));
    this.plusBtn?.addEventListener("click", () => this.changeQuantity(1));

    // Update button state on initialization
    this.updateButtonState();
  }

  changeQuantity(delta) {
    // If trying to decrease and allowRemove is false, prevent going below 1
    if (delta < 0 && !this.allowRemove && this.quantity <= 1) {
      return;
    }

    const newQty = Math.max(0, this.quantity + delta);
    this.updateQuantity(newQty);
  }

  async updateQuantity(newQuantity) {
    const loadingElement = document.querySelector(".cart-loader");
    loadingElement?.classList.add("active");

    this.minusBtn.disabled = true;
    this.plusBtn.disabled = true;

    if (this.loadingEvent) {
      window.dispatchEvent(
        new CustomEvent(this.loadingEvent, {
          bubbles: true,
          detail: {
            key: this.key,
            oldQuantity: this.quantity,
            newQuantity: newQuantity,
          },
        })
      );
    }

    const MAX_PER_VARIANT = 5;

    // Toast helper
    const showToast = (message, type = "info") => {
      try {
        const container = document.querySelector(".global-toast-container");
        if (!container) return;
        const toast = document.createElement("div");
        toast.className = `global-toast global-toast--${type}`;
        toast.textContent = message;
        container.appendChild(toast);
        setTimeout(() => toast.classList.add("global-toast--visible"), 50);
        setTimeout(() => {
          toast.classList.remove("global-toast--visible");
          setTimeout(() => toast.remove(), 300);
        }, 4000);
      } catch (err) {
        console.warn("Toast error", err);
      }
    };

    try {
      window.dispatchEvent(new CustomEvent("cart:loading"));

      const cartResp = await fetch("/cart.js");
      if (!cartResp.ok) throw new Error("Failed to fetch cart");
      const cartData = await cartResp.json();

      const freebieVariantId = window.FREEBIE_CONFIG?.variantId
        ? Number(window.FREEBIE_CONFIG.variantId)
        : null;

      const currentItem = cartData.items.find((it) => it.key === this.key);
      const variantId = currentItem ? Number(currentItem.variant_id) : null;
      const isFreebieItem = freebieVariantId && variantId === freebieVariantId;

      if (!isFreebieItem && newQuantity > 0) {
        const existingOtherLinesQty = cartData.items
          .filter((it) => it.key !== this.key)
          .filter((it) => Number(it.variant_id) === variantId)
          .reduce((sum, it) => sum + (it.quantity || 0), 0);

        const allowed = MAX_PER_VARIANT - existingOtherLinesQty;
        if (allowed <= 0) {
          showToast(
            "You already have the maximum quantity of this product variant in your cart.",
            "warning"
          );
          return;
        }

        if (newQuantity > allowed) {
          // showToast(
          //   `Quantity adjusted to ${allowed} due to a maximum of ${MAX_PER_VARIANT} per variant.`,
          //   "warning"
          // );
          showToast(
            `Maximum quantity for this product variant is ${MAX_PER_VARIANT}.`,
            "warning"
          );
          newQuantity = allowed;
        }
      }

      const updates = { [this.key]: newQuantity };

      const projectedItems = cartData.items.map((it) =>
        it.key === this.key ? { ...it, quantity: newQuantity } : { ...it }
      );

      const nonFreebieRemaining = projectedItems.some(
        (it) =>
          Number(it.variant_id) !== freebieVariantId &&
          Number(it.id) !== freebieVariantId &&
          it.quantity > 0
      );

      // If no non-freebie items remain → remove the freebie as well
      if (!nonFreebieRemaining && freebieVariantId) {
        const freebieLine = cartData.items.find(
          (it) =>
            Number(it.variant_id) === freebieVariantId ||
            Number(it.id) === freebieVariantId
        );
        if (freebieLine) {
          updates[freebieLine.key] = 0;
        }
      }

      // ✅ Single update call only
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (res.ok) {
        const cart = await res.json();

        window.dispatchEvent(
          new CustomEvent("cart:count-update", {
            detail: { count: cart.item_count },
          })
        );

        this.quantity = newQuantity;
        this.display.textContent = newQuantity;
        this.updateButtonState();

        this.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));

        if (this.customEvent) {
          window.dispatchEvent(
            new CustomEvent(this.customEvent, {
              bubbles: true,
              detail: {
                key: this.key,
                quantity: newQuantity,
                oldQuantity: this.quantity,
              },
            })
          );
        }
      } else {
        console.error("Cart update failed");
        showToast("Cart update failed", "error");
      }
    } catch (err) {
      console.error("Error updating cart:", err);
      showToast("Error updating cart", "error");
    } finally {
      this.minusBtn.disabled = false;
      this.plusBtn.disabled = false;
      loadingElement?.classList.remove("active");
      window.dispatchEvent(new CustomEvent("cart:loaded"));
    }
  }

  updateButtonState() {
    // Disable minus button if quantity is 1 and removal is not allowed
    if (!this.allowRemove && this.quantity <= 1) {
      this.minusBtn.disabled = true;
      this.minusBtn.setAttribute("aria-disabled", "true");
    } else {
      this.minusBtn.disabled = false;
      this.minusBtn.removeAttribute("aria-disabled");
    }
  }
}

customElements.define("quantity-selector", QuantitySelector);
